import { BitmapText, Container, Text, TextStyle } from 'pixi.js'
import { Spine } from '@esotericsoftware/spine-pixi'
import {
  clearSpaceBarEvent,
  numberAnimation,
  registerSpaceBarEvent,
  sleep,
  soundManager,
  spineManager,
  t,
} from '@slot-nx/core'
import { FreeGameType } from '../../game/model'
import { SceneContainerNameType } from '../../../components/Scene'
import gsap from 'gsap'
import { gameConfig } from '../../../config'
import { SpineGroupType } from '../../game/enum/SpineGroupType'
import { useGameStore } from '../../game/store/GameStore'
import { threecomboolympusMachine } from '../../machine/threecomboolympusMachine'
import { tableService } from '../../table/tableService'
import { preciseAdd } from '../../game/util/gameUtil'
import { GameSoundType } from '../../sound/GameSoundType'
import { videoRecordService } from '../../videoRecording'
import { ThreeComboOlympusSoundType } from '../../enum/ThreeComboOlympusSoundType'
import { registerNormalSpaceBarEvent } from '../../spin/SpinUtil'
import { winningService } from '../../winning/WinningService'

export class FreeGameService {
  public sceneContainer: Container
  public sceneSpine: Spine | null = null
  public freeSpinRoundContainer: Container
  // 建立 Spine
  private async createsceneSpine() {
    this.sceneSpine = null
    this.sceneSpine = spineManager.create('scene.skel', 'scene.atlas', 'spineScene', SpineGroupType.SceneSwitch)
    this.sceneSpine.y = gameConfig.resolution.height / 2
    this.sceneSpine.x = gameConfig.resolution.width / 2
    return this.sceneSpine
  }

  // 顯示 Panel
  public async showScenePanel() {
    await this.createsceneSpine()
    const sceneContainer = this.sceneContainer
    const [bgSpineContainer, tabContainer] = this.getContainers([SceneContainerNameType.BgSpine, SceneContainerNameType.TabAnywhere])
    const textObject = this.createTapAnyWhereText()
    bgSpineContainer.addChild(this.sceneSpine as Spine)
    tabContainer.addChild(textObject)
    this.tapAnywhereAnimation(textObject)
    sceneContainer.visible = true
  }

  // intro 動畫播完才能點擊關閉
  private enableSceneSkip() {
    const [bgSpineContainer, tabContainer] = this.getContainers([SceneContainerNameType.BgSpine, SceneContainerNameType.TabAnywhere])
    bgSpineContainer.eventMode = 'static'
    bgSpineContainer.cursor = 'pointer'
    const onSkip = async () => {
      await this.mgToBgEnd(bgSpineContainer, tabContainer)
    }
    registerSpaceBarEvent(onSkip)
    bgSpineContainer.onpointertap = onSkip
  }

  public async showRetriggerScene(freeGameType: number) {
    const sceneContainer = this.sceneContainer
    const soundType = this.retriggerSound(freeGameType) as ThreeComboOlympusSoundType
    const [bgSpineContainer] = this.getContainers([SceneContainerNameType.BgSpine])
    bgSpineContainer.eventMode = 'static'
    bgSpineContainer.cursor = 'pointer'
    soundManager.play(soundType)
    const spine = await this.createsceneSpine()
    const textObject = this.createTapAnyWhereText()
    bgSpineContainer.addChild(spine)
    this.tapAnywhereAnimation(textObject)
    sceneContainer.visible = true
  }

  private async mgToBgEnd(bgSpineContainer: Container, tabContainer: Container) {
    bgSpineContainer.onpointertap = null
    clearSpaceBarEvent()
    await this.playSceneOut()
    bgSpineContainer.removeChildren().forEach(c => c.destroy())
    tabContainer.removeChildren().forEach(c => c.destroy())
    spineManager.destroyByGroup(SpineGroupType.SceneSwitch)
    soundManager.switchBGM(GameSoundType.ThreeComboOlympus_BGBgm)
    soundManager.playBGM()
    threecomboolympusMachine.send({ type: 'free game start spin' })
  }

  private async fgToMgEnd(bgSpineContainer: Container, tabContainer: Container, totalWinAmount: Container) {
    const totalPayout = useGameStore.getState().gameResult.totalPayout
    const winBarText = winningService.winTextRef.getChildByName('winBarAmount') as Text
    const isExpand = useGameStore.getState().isExpand
    useGameStore.setState({ freeGameIndex: -1 })
    bgSpineContainer.onpointertap = null
    clearSpaceBarEvent()
    soundManager.play(GameSoundType.ThreeComboOlympus_BGIntroOut)
    this.fadeOutAndDestroy(totalWinAmount) // payout 改為淡出
    await playAnimation(this.sceneSpine as Spine, 'totalWin_out', false)
    winBarText.text = totalPayout
    this.sceneContainer.visible = false
    bgSpineContainer.removeChildren().forEach(c => c.destroy())
    tabContainer.removeChildren().forEach(c => c.destroy())
    spineManager.destroyByGroup(SpineGroupType.SceneSwitch)
    this.sceneSpine = null
    tableService.endFreeGame()
    tableService.potLvDown()
    this.closePotTips()
    if (isExpand) tableService.expandNormalTable()
    soundManager.switchBGM(GameSoundType.bgm)
    soundManager.playBGM()
    registerNormalSpaceBarEvent()
    threecomboolympusMachine.send({ type: 'close fg total win' })
  }

  // --- Scene Transition ---
  public async playSceneIn() {
    if (!this.sceneSpine) await this.createsceneSpine()
    await this.showScenePanel()
    soundManager.stopBGM()
    soundManager.play(GameSoundType.ThreeComboOlympus_BGIntroIn)
    soundManager.switchBGM(GameSoundType.ThreeComboOlympus_BGIntroIn_loop)
    soundManager.playBGM()
    const gameType = useGameStore.getState().freeGameType
    const typeName = FreeGameType[Number(gameType)]
    const inAnimation = `scene_in_${typeName}`
    const loopAnimation = `scene_loop_${typeName}`
    if (this.sceneSpine) {
      await playAnimation(this.sceneSpine, inAnimation, false)
      this.sceneSpine.state.setAnimation(0, loopAnimation, true)
    }
    this.enableSceneSkip()
  }

  public async playSceneOut() {
    const gameType = useGameStore.getState().freeGameType
    const typeName = FreeGameType[Number(gameType)]
    const animationName = `scene_out_${typeName}`
    tableService.startFreeGame()
    soundManager.play(GameSoundType.ThreeComboOlympus_BGIntroOut)
    await playAnimation(this.sceneSpine as Spine, animationName, false, () => this.sceneContainer.visible = false)
  }

  // --- Total Win ---
  public async playTotalWinIn() {
    const fgPayout = useGameStore.getState().gameResult.fgPayout
    const sceneContainer = this.sceneContainer
    const [bgSpineContainer, tabContainer, totalWinAmount] = this.getContainers([SceneContainerNameType.BgSpine, SceneContainerNameType.TabAnywhere, SceneContainerNameType.TotalWinAmount])
    bgSpineContainer.eventMode = 'static'
    bgSpineContainer.cursor = 'pointer'
    const spine = await this.createsceneSpine()
    const textObject = this.createTapAnyWhereText()
    bgSpineContainer.addChild(spine)
    tabContainer.addChild(textObject)
    this.tapAnywhereAnimation(textObject)
    sceneContainer.visible = true
    const totalWinText = new BitmapText(`0`, { fontName: 'fontC', fontSize: 32, align: 'center' })
    totalWinText.anchor.set(0.5)
    totalWinAmount.addChild(totalWinText)
    totalWinAmount.alpha = 1
    const [numTask, skipTask] = numberAnimation(0, fgPayout, 2, totalWinText, { maximumFractionDigits: 2, minimumFractionDigits: 2 })
    soundManager.play(GameSoundType.ThreeComboOlympus_TotalWin)
    await Promise.all([playAnimation(spine, 'totalWin_in', false, () => playAnimation(spine, 'totalWin_loop', true)), numTask])
    videoRecordService.stopRecordingInFreeGameTotalWin()
    registerSpaceBarEvent(() => this.fgToMgEnd(bgSpineContainer, tabContainer, totalWinAmount))
    bgSpineContainer.onpointertap = async () => {
      skipTask()
      await this.fgToMgEnd(bgSpineContainer, tabContainer, totalWinAmount)
    }
  }

  // --- Retrigger ---
  public async playRetrigger(fgAddTimes: number, freeGameType: number) {
    const freeGameConut = useGameStore.getState().freeGameConut
    const totalConut = preciseAdd(freeGameConut, fgAddTimes)
    if (!this.sceneSpine) await this.createsceneSpine()
    // retrigger 的動畫要用「觸發這次 retrigger 的那一局」的 type，
    // 不能讀 store——store 的 freeGameType 是上一局 spinIn 前寫入的，此刻還是舊值
    const animationName = `retrigger_${FreeGameType[Number(freeGameType)]}`
    soundManager.play(GameSoundType.ThreeComboOlympus_PotTrigger)
    this.showRetriggerScene(Number(freeGameType))
    await playAnimation(this.sceneSpine as Spine, animationName, false)
    await this.updateMoreFreeSpinRound(fgAddTimes, totalConut)
    await sleep(1200)
  }

  // --- Tap Text ---
  public tapAnywhereAnimation(text: Text) {
    const tl = gsap.timeline({
      repeat: Infinity,
      yoyo: true,
    })
    tl.fromTo(text, { alpha: 0.2 }, { duration: 1, alpha: 1, ease: 'sine.inOut' })
  }

  private createTapAnyWhereText() {
    const textObject = new Text()
    textObject.alpha = 0.2
    textObject.style = new TextStyle({
      fontSize: 48,
      fill: '#ffffff',
      align: 'center',
      fontWeight: '500',
      fontFamily: 'Roboto, Arial, sans-serif',
    })
    textObject.text = t('tap_to_continue')
    textObject.anchor.set(0.5)
    return textObject
  }

  private getContainers(names: SceneContainerNameType[]) {
    return names.map(name => this.sceneContainer.getChildByName(name) as Container)
  }

  public async updateMoreFreeSpinRound(current: number, totalTimes: number) {
    soundManager.play(GameSoundType.ThreeComboOlympus_Retirgger)
    const container = this.freeSpinRoundContainer.getChildByName('freeSpinRoundTextContainer') as BitmapText
    const [numTask] = numberAnimation(current, totalTimes, 0.2, container, { maximumFractionDigits: 0, minimumFractionDigits: 0 })
    await numTask
    useGameStore.setState({ freeGameConut: totalTimes })
  }

  public closePotTips() {
    tableService.potList.forEach((e) => {
      e.tipSpine?.state.setAnimation(0, 'close', true)
    })
  }

  public retriggerSound(freeGameType: number) {
    switch (freeGameType) {
      case FreeGameType.None:
        return null

      case FreeGameType.expend:
        return GameSoundType.ThreeComboOlympus_Expand

      case FreeGameType.wild:
        return GameSoundType.ThreeComboOlympus_Wild

      case FreeGameType.multiplier:
        return GameSoundType.ThreeComboOlympus_Multiplier

      default:
        return GameSoundType.ThreeComboOlympus_Dual
    }
  }

  public fadeOutAndDestroy(container: Container, duration = 0.3) {
    return new Promise<void>((resolve) => {
      gsap.to(container, {
        alpha: 0,
        duration,
        ease: 'power1.out',
        onComplete: () => {
          container.removeChildren().forEach(c => c.destroy())
          resolve()
        },
      })
    })
  }
}

function playAnimation(spine: Spine, animationName: string, loop: boolean, onComplete?: () => void): Promise<void> {
  return new Promise((resolve) => {
    spine.state.setAnimation(0, animationName, loop)
    if (!loop) {
      const listener = {
        complete: () => {
          spine.state.removeListener(listener)
          if (onComplete) onComplete()
          resolve()
        },
      }
      spine.state.addListener(listener)
    }
    else {
      resolve()
    }
  })
}
